// src/redux/slices/siteContentSlice.ts
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { SiteContent } from '../../types/siteContent';
import type { RootState } from '../store';

interface SiteContentState {
  data: SiteContent | null;
  fetchLoading: boolean;
  updateLoading: boolean;
  publishLoading: boolean;
  error: string | null;
}

const initialState: SiteContentState = {
  data: null,
  fetchLoading: false,
  updateLoading: false,
  publishLoading: false,
  error: null,
};

export const siteContentSlice = createSlice({
  name: 'siteContent',
  initialState,
  reducers: {
    fetchSiteContentRequest: state => {
      state.fetchLoading = true;
      state.error = null;
    },
    fetchSiteContentSuccess: (state, action: PayloadAction<SiteContent>) => {
      state.fetchLoading = false;
      state.data = action.payload ? structuredClone(action.payload) : null;
      state.error = null;
    },
    fetchSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.fetchLoading = false;
      state.error = action.payload;
      state.data = null;
    },

    // Update widget data - Proper Immer pattern (in-place mutation)
    updateWidgetData: (state, action: PayloadAction<{ index: number; data: any }>) => {
      const { index, data } = action.payload;

      if (state.data?.widgets && state.data.widgets[index]) {
        // Mutate in place (Immer tracks this safely)
        state.data.widgets[index].data = data;

        if (state.data.homeWidgets) {
          const homeWidgetIndex = state.data.homeWidgets.findIndex(
            (widget: any) => widget.type === state.data!.widgets![index].type
          );

          if (homeWidgetIndex >= 0) {
            state.data.homeWidgets[homeWidgetIndex].data = data;
          }
        }
      }
    },

    // Add new widget section
    addWidgetSection: (state, action: PayloadAction<{ widget: any; afterIndex?: number }>) => {
      if (state.data?.widgets) {
        const { widget, afterIndex } = action.payload;
        
        if (afterIndex !== undefined && afterIndex >= 0) {
          // Add to both arrays at the same position
          state.data.widgets.splice(afterIndex + 1, 0, widget);
          if (state.data.homeWidgets) {
            state.data.homeWidgets.splice(afterIndex + 1, 0, widget);
          }
        } else {
          // Add at the end of both arrays
          state.data.widgets.push(widget);
          if (state.data.homeWidgets) {
            state.data.homeWidgets.push(widget);
          }
        }
      }
    },

    // Delete widget section
    deleteWidgetSection: (state, action: PayloadAction<number>) => {
      if (state.data?.widgets && state.data.widgets[action.payload]) {
        const widgetToDelete = state.data.widgets[action.payload];
        
        // Remove from main widgets array
        state.data.widgets.splice(action.payload, 1);
        
        // Also remove from homeWidgets array if it exists
        if (state.data.homeWidgets) {
          const homeWidgetIndex = state.data.homeWidgets.findIndex(
            (widget: any) => widget.type === widgetToDelete.type &&
            JSON.stringify(widget.data) === JSON.stringify(widgetToDelete.data)
          );
          
          if (homeWidgetIndex >= 0) {
            state.data.homeWidgets.splice(homeWidgetIndex, 1);
          }
        }
      }
    },

    // Update site content actions
    updateSiteContentRequest: state => {
      state.updateLoading = true;
      state.error = null;
    },
    updateSiteContentSuccess: (state, action: PayloadAction<SiteContent>) => {
      state.updateLoading = false;
      state.data = action.payload ? structuredClone(action.payload) : null;
      state.error = null;
    },
    updateSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.updateLoading = false;
      state.error = action.payload;
    },

    // Publish site content actions
    publishSiteContentRequest: state => {
      state.publishLoading = true;
      state.error = null;
    },
    publishSiteContentSuccess: (state) => {
      state.publishLoading = false;
      state.error = null;
      // Don't update state.data - let fetchSiteContentSuccess handle that
    },
    publishSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.publishLoading = false;
      state.error = action.payload;
    },

    // Clear site content
    clearSiteContent: state => {
      state.data = null;
      state.error = null;
      state.fetchLoading = false;
      state.updateLoading = false;
      state.publishLoading = false;
    },

    // Manual reset for stuck loading states
    resetLoadingStates: state => {
      state.fetchLoading = false;
      state.updateLoading = false;
      state.publishLoading = false;
    },

  },
});

export const {
  fetchSiteContentRequest,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
  updateWidgetData,
  addWidgetSection,
  deleteWidgetSection,
  updateSiteContentRequest,
  updateSiteContentSuccess,
  updateSiteContentFailure,
  publishSiteContentRequest,
  publishSiteContentSuccess,
  publishSiteContentFailure,
  clearSiteContent,
  resetLoadingStates,
} = siteContentSlice.actions;

// Safe Selector (Deep clone result)
export const selectPlainSiteContent = createSelector(
  (state: RootState) => state.siteContent.data,
  (data) => data ? JSON.parse(JSON.stringify(data)) : null
);

export default siteContentSlice.reducer;
